<script setup>
/**
 * SuperTVGo: créditos dos painéis de revenda na tela inicial — IPTV (Atmos)
 * e UniTV, este separado em Mensal e Anual.
 *
 * Nunca atualiza sozinho (pedido do usuário, 2026-10-03): mostra o último
 * valor salvo na conta e só consulta os painéis quando alguém aperta
 * "Atualizar". A consulta roda em background no servidor (o Atmos leva
 * 30-60s); enquanto isso o botão fica em loading e o card confere o
 * resultado de tempos em tempos.
 */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import SupertvgoSaldosAPI from 'dashboard/api/supertvgoSaldos';
import Button from 'dashboard/components-next/button/Button.vue';

const INTERVALO_CONFERENCIA_MS = 4000;

const TEXTOS = {
  titulo: 'Créditos nos painéis',
  carregandoValor: '…',
  semData: 'Ainda não atualizado',
  consultando: 'Consultando os painéis… pode levar até 1 minuto.',
};

const saldos = ref({});
const carregando = ref(true);
const erroLocal = ref('');
let temporizador = null;

const atualizando = computed(() => Boolean(saldos.value.atualizando));

const formatarData = iso => {
  if (!iso) return '';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '';
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatarNumero = valor =>
  valor === undefined || valor === null
    ? '—'
    : Number(valor).toLocaleString('pt-BR');

const itens = computed(() => {
  const { iptv = {}, unitv = {} } = saldos.value;
  return [
    {
      chave: 'iptv',
      titulo: 'IPTV (Atmos)',
      valor: formatarNumero(iptv.creditos),
      data: formatarData(iptv.atualizado_em),
    },
    {
      chave: 'unitv-mensal',
      titulo: 'UniTV Mensal',
      valor: formatarNumero(unitv.mensal),
      data: formatarData(unitv.atualizado_em),
    },
    {
      chave: 'unitv-anual',
      titulo: 'UniTV Anual',
      valor: formatarNumero(unitv.anual),
      data: formatarData(unitv.atualizado_em),
    },
  ];
});

const pararConferencia = () => {
  if (temporizador) {
    clearTimeout(temporizador);
    temporizador = null;
  }
};

const carregar = async () => {
  try {
    const { data } = await SupertvgoSaldosAPI.get();
    saldos.value = data || {};
    erroLocal.value = '';
  } catch (error) {
    erroLocal.value = 'Não foi possível carregar os saldos.';
  } finally {
    carregando.value = false;
  }
};

const conferirAteTerminar = () => {
  pararConferencia();
  if (!atualizando.value) return;
  temporizador = setTimeout(async () => {
    await carregar();
    conferirAteTerminar();
  }, INTERVALO_CONFERENCIA_MS);
};

const atualizar = async () => {
  if (atualizando.value) return;
  saldos.value = { ...saldos.value, atualizando: true, erro: null };
  try {
    const { data } = await SupertvgoSaldosAPI.atualizar();
    saldos.value = data || {};
    erroLocal.value = '';
  } catch (error) {
    saldos.value = { ...saldos.value, atualizando: false };
    erroLocal.value = 'Não foi possível iniciar a atualização.';
  }
  conferirAteTerminar();
};

onMounted(async () => {
  await carregar();
  // Alguém já tinha apertado "Atualizar" (outra aba, outro atendente):
  // acompanha até terminar em vez de mostrar o valor velho como pronto.
  conferirAteTerminar();
});

onBeforeUnmount(pararConferencia);
</script>

<template>
  <div class="w-full max-w-xl px-4" data-testid="supertvgo-saldos-creditos">
    <div class="flex items-center justify-between mb-3">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ TEXTOS.titulo }}
      </h3>
      <Button
        label="Atualizar"
        icon="i-lucide-refresh-cw"
        size="sm"
        variant="faded"
        color="slate"
        :is-loading="atualizando"
        :disabled="atualizando || carregando"
        @click="atualizar"
      />
    </div>
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div
        v-for="item in itens"
        :key="item.chave"
        class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-2"
      >
        <span class="text-xs text-n-slate-11">{{ item.titulo }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">
          {{ carregando ? TEXTOS.carregandoValor : item.valor }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{ item.data ? `Atualizado em ${item.data}` : TEXTOS.semData }}
        </span>
      </div>
    </div>
    <p v-if="atualizando" class="mt-3 text-xs text-n-slate-11">
      {{ TEXTOS.consultando }}
    </p>
    <p v-else-if="saldos.erro || erroLocal" class="mt-3 text-xs text-n-ruby-11">
      {{ erroLocal || saldos.erro }}
    </p>
  </div>
</template>
